import * as React from 'react';
import { useEmailAttachmentCreateMutation } from '../../../../queries/Email/useEmailAttachmentCreateMutation';
import { getApiErrorMessage } from '../../../../utils/Support/supportHelpers';
import type { PendingAttachment } from './types';

export const useComposerAttachments = () => {
	const [pendingAttachments, setPendingAttachments] = React.useState<PendingAttachment[]>([]);
	// Support has no MutationCache/toast, so a failed upload must surface inline — otherwise the
	// row silently vanishes from the pending list with no explanation.
	const [uploadError, setUploadError] = React.useState<string | null>(null);
	const fileInputRef = React.useRef<HTMLInputElement>(null);
	// Monotonic counter for unique temp ids. `Date.now()` alone collides when two
	// same-named files are selected in the same tick, which previously caused one
	// upload to never reconcile to its server id and be dropped from the reply.
	const attachmentSeqRef = React.useRef(0);

	// Reconciliation and error surfacing are done per-upload (keyed on the row's temp id) via the
	// per-call callbacks in handleFileSelect; the mutation requires hook-level handlers, so they no-op.
	const uploadAttachment = useEmailAttachmentCreateMutation({
		onSuccess: () => undefined,
		onError: () => undefined,
	});

	const addFiles = (files: File[]) => {
		if (files.length === 0) return;

		// A new selection starts a fresh attempt, so drop any error from the previous one.
		setUploadError(null);

		files.forEach(file => {
			const tempId = `temp-${attachmentSeqRef.current++}-${file.name}`;
			setPendingAttachments(prev => [
				...prev,
				{ id: tempId, fileName: file.name, fileSize: file.size, uploading: true },
			]);
			// Match on the unique temp id (not fileName) so duplicate-named uploads
			// each reconcile to their own server id instead of colliding.
			uploadAttachment.mutate(file, {
				onSuccess: data => {
					setPendingAttachments(prev => prev.map(a => (a.id === tempId ? { ...a, id: data.id, uploading: false } : a)));
				},
				onError: error => {
					setPendingAttachments(prev => prev.filter(a => a.id !== tempId));
					setUploadError(getApiErrorMessage(error, `Failed to upload "${file.name}". Please try again.`));
				},
			});
		});
	};

	const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = e.target.files;
		if (!files || files.length === 0) return;
		addFiles(Array.from(files));
		// Reset the input so the same file can be re-selected
		e.target.value = '';
	};

	const removeAttachment = (id: string) => {
		setPendingAttachments(prev => prev.filter(a => a.id !== id));
	};

	const clearAttachments = () => {
		setPendingAttachments([]);
		setUploadError(null);
	};

	const dismissUploadError = () => setUploadError(null);

	const hasUploading = pendingAttachments.some(a => a.uploading);

	return {
		addFiles,
		clearAttachments,
		dismissUploadError,
		fileInputRef,
		handleFileSelect,
		hasUploading,
		pendingAttachments,
		removeAttachment,
		uploadError,
	};
};
